import React, { useEffect } from 'react';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@components/error/src/error-message';
import { max } from '@utils/validations/max';
import { min } from '@utils/validations/min';
import classNames from 'classnames';

import { InputCounterProps } from './input-counter.type';

export const InputCounter = ({
  title,
  error,
  formCounterName,
  maxQuantity = 0,
  minQuantity = 0,
}: InputCounterProps) => {
  const { control, setValue } = useFormContext();

  const handleSubstract = (field: ControllerRenderProps<FieldValues, string>) => {
    field.value >= minQuantity && field.onChange((field.value as number) - 1);
  };

  const handleAdd = (field: ControllerRenderProps<FieldValues, string>) => {
    field.value < maxQuantity && field.onChange((field.value as number) + 1);
  };

  const calculateValue = (value: string) => {
    const numericValue = parseInt(value, 10);

    if (isNaN(numericValue) || numericValue < minQuantity) return minQuantity;
    if (numericValue > maxQuantity) return maxQuantity;
    return numericValue;
  };

  const handleChange = (field: ControllerRenderProps<FieldValues, string>, value: string) => {
    if (value === '') {
      field.onChange(value);
      return;
    }

    field.onChange(calculateValue(value));
  };

  useEffect(() => {
    if (minQuantity) setValue(formCounterName, minQuantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minQuantity]);

  return (
    <div>
      <div className='flex items-center justify-between'>
        <p className='self-start text-primary text-subtitle-l'>{title}</p>
        <Controller
          name={formCounterName}
          control={control}
          rules={{
            required: error,
            validate: {
              min: (value) => min(value, minQuantity, error),
              max: (value) => max(value, maxQuantity, error),
            },
          }}
          render={({ field }) => (
            <div className='flex items-center gap-4 font-bold text-xl'>
              <button
                type='button'
                className={classNames('w-9 h-9 rounded-full', {
                  'fill-tertiary focus:fill-tertiary-active text-secondary focus:text-primary':
                    field.value !== minQuantity,
                  'fill-tertiary-disabled text-soft cursor-none pointer-events-none': field.value === minQuantity,
                })}
                onClick={() => handleSubstract(field)}>
                -
              </button>
              <input
                type='text'
                className='bg-primary text-primary text-subtitle-l w-6 text-center border-0 p-0'
                value={field.value}
                onChange={(e) => handleChange(field, e.target.value)}
                onBlur={(e) => handleChange(field, e.target.value)}
              />
              <button
                type='button'
                className={classNames('w-9 h-9 rounded-full', {
                  'fill-tertiary focus:fill-tertiary-active text-secondary focus:text-primary':
                    field.value !== maxQuantity,
                  'fill-tertiary-disabled text-soft cursor-none pointer-events-none': field.value === maxQuantity,
                })}
                onClick={() => handleAdd(field)}>
                +
              </button>
            </div>
          )}
        />
      </div>
      <ErrorMessage name={formCounterName} />
    </div>
  );
};
